import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import {
  IOnboardingSettings,
  IOnboardingDocument,
  IOnboardingTemplateType,
  OnboardingDocumentType
} from 'core/interfaces/onboardingInterface';

const normalizeOnboardingSettings = (raw: unknown): IOnboardingSettings | null => {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const settings = raw as IOnboardingSettings;

  return {
    ...settings,
    storageProvider:
      (record.storageProvider as string | null | undefined) ??
      (record.storage_provider as string | null | undefined) ??
      null,
    provider: (record.provider as IOnboardingSettings['provider']) ?? null,
  };
};

// ========================
// General Settings (EIN + Storage Provider)
// ========================

/**
 * Fetches the onboarding settings for the current (or specified) workplace.
 * Returns the first settings record found, or `null` if none exist.
 * @param workplaceId - Optional workplace ID override. Falls back to `localStorage`.
 */
export const getOnboardingSettings = async (workplaceId?: number): Promise<IOnboardingSettings | null> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    params.append('page', '1');
    params.append('itemsPerPage', '10');
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    
    const response = await apiService.get(`onboarding_settings?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingSettings[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items.length > 0 ? normalizeOnboardingSettings(items[0]) : null;
  } catch (error) {
    console.error('Error fetching onboarding settings:', error);
    return null;
  }
};

/**
 * Creates a new onboarding settings record.
 * @param settings - Partial settings object to persist.
 * @returns The created {@link IOnboardingSettings} record.
 */
export const createOnboardingSettings = async (
  settings: Partial<IOnboardingSettings>
): Promise<IOnboardingSettings> => {
  const response = await apiService.post('onboarding-settings', settings);
  const data = apiService.responseHandler(response);
  const normalized = normalizeOnboardingSettings(data);
  if (!normalized) {
    throw new Error('Invalid onboarding settings response');
  }
  return normalized;
};

// ========================
// Onboarding Template Types
// ========================

/**
 * Fetches all available onboarding template types (e.g. W4, I9, Direct Deposit).
 * Pagination is disabled — all records are returned in a single request.
 * @returns An array of {@link IOnboardingTemplateType} objects.
 */
export const getOnboardingTemplateTypes = async (): Promise<IOnboardingTemplateType[]> => {
  try {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    
    const response = await apiService.get(`onboarding_template_types?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingTemplateType[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding template types:', error);
    return [];
  }
};

// ========================
// Onboarding Documents
// ========================

export const getOnboardingTemplateDocumentUrl = (filePath?: string): string | null => {
  if (!filePath) return null;
  const base = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
  const path = filePath.replace(/^\//, '');
  return `${base}/onboarding_templates/${path}`;
};

export const getOnboardingDocuments = async (workplaceId?: number): Promise<IOnboardingDocument[]> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    params.append('order[sortOrder]', 'asc');
    
    const response = await apiService.get(`onboarding_document_templates?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: IOnboardingDocument[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding documents:', error);
    return [];
  }
};

export const createOnboardingDocument = async (
  document: FormData
): Promise<IOnboardingDocument> => {
  const response = await apiService.postUpload('onboarding-document-templates', document);
  const data = apiService.responseHandler(response);
  return data;
};

export const updateOnboardingDocument = async (
  id: number,
  document: Partial<IOnboardingDocument>,
  file?: File
): Promise<IOnboardingDocument> => {
  // If file is provided, use FormData for POST with file upload
  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    if (document.name) formData.append('name', document.name);
    if (document.type) formData.append('type', document.type);
    if (document.sortOrder !== undefined) formData.append('sortOrder', document.sortOrder.toString());
    if (document.isManagerSignatureRequired !== undefined) {
      formData.append('isManagerSignatureRequired', document.isManagerSignatureRequired.toString());
    }
    if (document.workplace) formData.append('workplace', document.workplace);
    
    const response = await apiService.postUpload(`onboarding-document-templates/${id}`, formData);
    const data = apiService.responseHandler(response);
    return data;
  }
  
  // Otherwise, send JSON data via PATCH with only allowed fields
  const patchPayload: {
    workplace?: string;
    name?: string;
    type?: string;
    sortOrder?: number;
    active?: boolean;
    isManagerSignatureRequired?: boolean;
  } = {};
  
  if (document.workplace !== undefined) patchPayload.workplace = document.workplace;
  if (document.name !== undefined) patchPayload.name = document.name;
  if (document.type !== undefined) patchPayload.type = document.type;
  if (document.sortOrder !== undefined) patchPayload.sortOrder = document.sortOrder;
  if (document.active !== undefined) patchPayload.active = document.active;
  if (document.isManagerSignatureRequired !== undefined) patchPayload.isManagerSignatureRequired = document.isManagerSignatureRequired;
  
  const response = await apiService.patch(`onboarding-document-templates/${id}/toggle-active`, patchPayload);
  const data = apiService.responseHandler(response);
  return data;
};

export const deleteOnboardingDocument = async (id: number): Promise<void> => {
  await apiService.delete(`onboarding_document_templates/${id}`);
};

export const reorderOnboardingDocuments = async (
  documents: { id: number; sortOrder: number }[]
): Promise<void> => {
  // Update each document's sort order
  const updatePromises = documents.map(doc =>
    updateOnboardingDocument(doc.id, { sortOrder: doc.sortOrder })
  );
  await Promise.all(updatePromises);
};

// Helper function to map type IRI to enum
export const getDocumentTypeFromIRI = (typeIRI: string): OnboardingDocumentType => {
  if (typeIRI.includes('/1')) return OnboardingDocumentType.DATA_ENTRY;
  if (typeIRI.includes('/2')) return OnboardingDocumentType.UPLOAD;
  if (typeIRI.includes('/3')) return OnboardingDocumentType.POLICY;
  return OnboardingDocumentType.DATA_ENTRY; // default
};

// Helper function to get type IRI from enum
export const getDocumentTypeIRI = (documentType: OnboardingDocumentType): string => {
  const typeMap = {
    [OnboardingDocumentType.DATA_ENTRY]: '/api/onboarding_template_types/1',
    [OnboardingDocumentType.UPLOAD]: '/api/onboarding_template_types/2',
    [OnboardingDocumentType.POLICY]: '/api/onboarding_template_types/3',
  };
  return typeMap[documentType];
};

export const uploadOnboardingDocumentFile = async (
  file: File,
  name: string,
  typeId: number,
  sortOrder: number,
  isManagerSignatureRequired: boolean = false,
  workplaceId?: number
): Promise<IOnboardingDocument> => {
  const id = workplaceId || Number(localStorage.getItem("workplace-id"));
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  formData.append('type', `/api/onboarding_template_types/${typeId}`);
  formData.append('sortOrder', sortOrder.toString());
  formData.append('isManagerSignatureRequired', isManagerSignatureRequired.toString());
  formData.append('workplace', `/api/workplaces/${id}`);
  
  return createOnboardingDocument(formData);
};

// ========================
// Onboarding Records
// ========================

export interface CreateOnboardingPayload {
  user?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  cellPhone: string;
  type: string;
  workplace: string;
  employmentStartDate: string;
  payType: string;
  employmentType: string;
  securityRole: string;
}

export const createOnboarding = async (
  payload: CreateOnboardingPayload
): Promise<any> => {
  const response = await apiService.post('onboardings', payload);
  const data = apiService.responseHandler(response);
  return data;
};

export interface OnboardingUserReference {
  '@id'?: string;
  '@type'?: string;
  id?: number | string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
}

export type OnboardingUserField = string | OnboardingUserReference;

export interface OnboardingRecord {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  user?: OnboardingUserField;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  cellPhone: string;
  type: string;
  status: string;
  workplace: string;
  employmentStartDate: string;
  payType: string;
  employmentType: string;
  securityRole: string;
  startedBy?: OnboardingUserField;
  initiatedBy?: OnboardingUserField;
  createdBy?: OnboardingUserField;
  phase1CompletedAt?: string;
  phase2CompletedAt?: string;
  phase2ReviewedAt?: string;
  phase2ReviewStatus?: 'approved' | 'rejected' | 'pending_approval';
  phase3CompletedAt?: string;
  // Basic info fields (populated after employee fills form)
  gender?: string;
  maritalStatus?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  // Phase Two Status (embedded)
  phaseTwoStatuses?: OnboardingPhaseTwoStatus[];
}

export interface OnboardingListResponse {
  member?: OnboardingRecord[];
  'hydra:member'?: OnboardingRecord[];
  'hydra:totalItems'?: number;
  totalItems?: number;
}

export const getOnboardings = async (
  workplaceId?: number,
  _page: number = 1,
  itemsPerPage: number = 30,
): Promise<OnboardingListResponse> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    const params = new URLSearchParams();
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    params.append('order[id]', 'desc');

    const data = await paginationService.getWithHandler(
      `onboardings?${params.toString()}`,
      undefined,
      itemsPerPage,
    );
    return data;
  } catch (error) {
    console.error('Error fetching onboardings:', error);
    return { member: [] };
  }
};

export const deleteOnboarding = async (id: number): Promise<void> => {
  await apiService.delete(`onboardings/${id}/delete-with-user`);
};

// ========================
// Onboarding Phase Two (Employee Self-Entry)
// ========================

export interface OnboardingPhaseTwoDocumentStatus {
  template_id: number;
  name: string;
  type: string;
  status: string;
  path: string;
}

export interface OnboardingPhaseTwoStatus {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  onboarding: string;
  user: string;
  progress: {
    basic_info: {
      status: string;
      editable: boolean;
    };
    documents: OnboardingPhaseTwoDocumentStatus[];
  };
  updatedAt: string;
}

export const getOnboardingPhaseTwoStatus = async (): Promise<OnboardingPhaseTwoStatus | null> => {
  try {
    const response = await apiService.get('onboarding-phase-two/status');
    const data = apiService.responseHandler(response);
    return data;
  } catch (error) {
    console.error('Error fetching onboarding phase two status:', error);
    return null;
  }
};

export const uploadOnboardingPhaseTwoDocument = async (
  file: File,
  onboardingId: number,
  templateId: number
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('onboarding', onboardingId.toString());
  formData.append('template', templateId.toString());
  
  const response = await apiService.postUpload('onboarding-phase-two/upload', formData);
  const data = apiService.responseHandler(response);
  return data;
};

export const submitOnboardingPhaseTwo = async (onboardingId: number): Promise<any> => {
  const response = await apiService.post(`onboardings/${onboardingId}/submit-phase-two`, {});
  const data = apiService.responseHandler(response);
  return data;
};

export const getOnboardingById = async (id: number): Promise<OnboardingRecord | null> => {
  try {
    const response = await apiService.get(`onboardings/${id}`);
    const data = apiService.responseHandler(response);
    return data;
  } catch (error) {
    console.error('Error fetching onboarding by ID:', error);
    return null;
  }
};

export interface BasicInfoPayload {
  firstName: string;
  middleName?: string;
  lastName: string;
  cellPhone: string;
  gender: string;
  maritalStatus: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
  state: string;
  country: string;
  dateOfBirth: string;
}

export const updateOnboardingBasicInfo = async (
  onboardingId: number,
  payload: BasicInfoPayload
): Promise<any> => {
  const response = await apiService.patch(`onboardings/${onboardingId}/basic_info`, payload);
  const data = apiService.responseHandler(response);
  return data;
};

// ========================
// Employee Uploaded Documents
// ========================

export interface OnboardingDocumentTemplateRef {
  '@id': string;
  '@type': string;
  id: number;
  workplace: string;
  name: string;
  filePath: string;
  isManagerSignatureRequired: boolean;
}

export interface OnboardingEmployeeDocument {
  '@context'?: string;
  '@id'?: string;
  '@type'?: string;
  id: number;
  onboarding: string; // IRI like "/api/onboardings/123"
  template: string | OnboardingDocumentTemplateRef; // IRI or full object
  filePath: string;
  uploadedAt: string;
  
  // Manager approval fields
  managerApproved?: boolean;
  managerApprovedAt?: string;
  
  // Legacy manager attestation fields (deprecated)
  requiresManagerAttestation?: boolean;
  managerAttestation?: {
    attested: boolean;
    attestedAt?: string;
    attestedBy?: string; // Manager user IRI
    signaturePath?: string;
    attestedDocumentPath?: string;
  };
}

export const getOnboardingEmployeeDocuments = async (
  onboardingId: number
): Promise<OnboardingEmployeeDocument[]> => {
  try {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    params.append('onboarding', `/api/onboardings/${onboardingId}`);
    
    const response = await apiService.get(`onboarding_employee_documents?${params.toString()}`);
    const data = apiService.responseHandler(response);
    
    let items: OnboardingEmployeeDocument[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && data.member) {
      items = Array.isArray(data.member) ? data.member : [data.member];
    } else if (data && data['hydra:member']) {
      items = Array.isArray(data['hydra:member']) ? data['hydra:member'] : [data['hydra:member']];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching onboarding employee documents:', error);
    return [];
  }
};

// Helper function to extract ID from IRI
export const extractIdFromIri = (iri: string): number | null => {
  if (!iri) return null;
  const match = iri.match(/\/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

// ========================
// Phase 3: Manager Review Actions
// ========================

export type Phase2ReviewAction = 'approve' | 'delete' | 'reset' | 'reset_partial';

export interface Phase2ReviewPayload {
  action: Phase2ReviewAction;
  template_ids?: number[];
}

export const submitPhase2Review = async (
  onboardingId: number,
  action: Phase2ReviewAction,
  templateIds?: number[]
): Promise<any> => {
  const payload: Phase2ReviewPayload = { action };
  if (templateIds && templateIds.length > 0) {
    payload.template_ids = templateIds;
  }
  const response = await apiService.patch(`onboardings/${onboardingId}/phase2-review`, payload);
  const data = apiService.responseHandler(response);
  return data;
};

// Legacy functions for backward compatibility
export const approveOnboarding = async (onboardingId: number): Promise<any> => {
  return submitPhase2Review(onboardingId, 'approve');
};

export const rejectOnboarding = async (onboardingId: number): Promise<any> => {
  return submitPhase2Review(onboardingId, 'delete');
};

export const resetOnboarding = async (onboardingId: number): Promise<any> => {
  return submitPhase2Review(onboardingId, 'reset');
};

export const resetOnboardingPartial = async (
  onboardingId: number,
  templateIds: number[]
): Promise<any> => {
  return submitPhase2Review(onboardingId, 'reset_partial', templateIds);
};

// ========================
// Manager Document Attestation
// ========================

export interface AttestedDocumentResponse {
  signaturePath?: string; // Optional since signature is now embedded in the PDF
  documentPath: string;
  attestedAt: string;
}

export const uploadAttestedDocument = async (
  attestedFile: File,
  onboardingId: number,
  documentId: string | number
): Promise<AttestedDocumentResponse> => {
  const formData = new FormData();
  formData.append('file', attestedFile);
  formData.append('documentId', documentId.toString());
  
  const response = await apiService.postUpload('onboarding-manager-approve/upload', formData);
  const data = apiService.responseHandler(response);
  return data;
};
